import * as React from "react";
import { ScrollArea as BaseScrollArea } from "@base-ui/react/scroll-area";
import { useTouchPrimary } from "~/hooks/use-touch-primary";
import { useShape } from "~/lib/shape-context";
import { cn } from "~/lib/utils";

type ScrollAreaOrientation = "vertical" | "horizontal" | "both";

const ScrollAreaContext = React.createContext(false);

const viewportOverflowClassName: Record<ScrollAreaOrientation, string> = {
  both: "overflow-auto",
  horizontal: "overflow-x-auto overflow-y-hidden",
  vertical: "overflow-x-hidden overflow-y-auto",
};

// Base UI sets an inline `min-width: fit-content` on Content so horizontal
// scroll can measure overflow. With vertical-only scrolling that lets
// unbreakable children widen the content past the clipped viewport and
// defeats `truncate`/`min-w-0` layouts, so undo it for that orientation.
const verticalContentStyle: React.CSSProperties = { minWidth: 0 };

// The root is a grid so the viewport stretches to the root's used height.
// The viewport's own `size-full` percentage cannot resolve when the root is
// flex-sized under a max-height cap (e.g. a popover panel), which left the
// viewport at content height — nothing to scroll, overflow just clipped.
const rootClassName = "relative grid overflow-hidden";

interface ScrollAreaProps extends Omit<
  React.ComponentPropsWithoutRef<typeof BaseScrollArea.Root>,
  "children" | "className" | "style"
> {
  children: React.ReactNode;
  className?: string;
  orientation?: ScrollAreaOrientation;
  style?: React.CSSProperties;
  viewportClassName?: string;
}

interface ScrollBarProps {
  orientation: "horizontal" | "vertical";
}

function ScrollBar({ orientation }: ScrollBarProps) {
  const isTouch = React.useContext(ScrollAreaContext);
  const shape = useShape();

  if (isTouch) {
    return null;
  }

  return (
    <BaseScrollArea.Scrollbar
      className={cn(
        "group/scrollbar absolute z-20 flex touch-none select-none opacity-0 transition-opacity duration-120 ease-out delay-160 data-[hovering]:opacity-100 data-[scrolling]:opacity-100 data-[hovering]:duration-160 data-[scrolling]:duration-160 data-[hovering]:delay-0 data-[scrolling]:delay-0",
        orientation === "vertical"
          ? "top-0 right-0 h-full w-2.5"
          : "bottom-0 left-0 h-2.5 w-full flex-col",
      )}
      orientation={orientation}
    >
      <BaseScrollArea.Thumb
        className={cn(
          "relative bg-foreground/25 transition-[background-color,width,height] duration-160 ease-in-out group-hover/scrollbar:bg-foreground/45 active:!bg-foreground/60",
          shape.bg,
          orientation === "vertical"
            ? "mx-auto my-1 h-[var(--scroll-area-thumb-height)] w-1 group-hover/scrollbar:w-1.5"
            : "mx-1 my-auto h-1 w-[var(--scroll-area-thumb-width)] group-hover/scrollbar:h-1.5",
        )}
      />
    </BaseScrollArea.Scrollbar>
  );
}

const ScrollArea = React.forwardRef<HTMLDivElement, ScrollAreaProps>(
  function ScrollArea(
    {
      children,
      className,
      orientation = "vertical",
      viewportClassName,
      ...props
    },
    ref,
  ) {
    const isTouch = useTouchPrimary();
    const viewportClass = cn(
      "size-full max-h-[inherit] max-w-[inherit] rounded-[inherit]",
      viewportOverflowClassName[orientation],
      viewportClassName,
    );

    if (isTouch) {
      return (
        <ScrollAreaContext.Provider value={isTouch}>
          <div
            role="group"
            aria-roledescription="scroll area"
            className={cn(rootClassName, className)}
            data-slot="scroll-area"
            ref={ref}
            {...props}
          >
            <div
              className={viewportClass}
              data-slot="scroll-area-viewport"
              tabIndex={0}
            >
              {children}
            </div>
          </div>
        </ScrollAreaContext.Provider>
      );
    }

    return (
      <ScrollAreaContext.Provider value={isTouch}>
        <BaseScrollArea.Root
          className={cn(rootClassName, className)}
          data-slot="scroll-area"
          ref={ref}
          {...props}
        >
          <BaseScrollArea.Viewport
            className={viewportClass}
            data-slot="scroll-area-viewport"
          >
            <BaseScrollArea.Content
              data-slot="scroll-area-content"
              style={
                orientation === "vertical" ? verticalContentStyle : undefined
              }
            >
              {children}
            </BaseScrollArea.Content>
          </BaseScrollArea.Viewport>
          {(orientation === "vertical" || orientation === "both") && (
            <ScrollBar orientation="vertical" />
          )}
          {(orientation === "horizontal" || orientation === "both") && (
            <ScrollBar orientation="horizontal" />
          )}
          {orientation === "both" ? (
            <BaseScrollArea.Corner className="absolute right-0 bottom-0 z-20 size-2.5 bg-transparent" />
          ) : null}
        </BaseScrollArea.Root>
      </ScrollAreaContext.Provider>
    );
  },
);

ScrollArea.displayName = "ScrollArea";

export { ScrollArea, ScrollAreaContext };
export type { ScrollAreaOrientation };
